/// <reference lib="dom"/>
/** @jsx hh */
/** @jsxFrag ChiFragment */
import { ChiFragment, hh } from "@chi/chi";
import { useMemo } from "@chi/chi/hooks";
import type { ParamDict, ParamDictProps, ParamList } from "@utils/params";
import { ParseNumbers, type SynthDef, SynthOpts, type SynthVoiceProps } from "@/synth/synth.tsx";
import { FadeEnv, MultiOsc, OscTypes } from "@/synth/blocks.tsx";

//#region tone
export const ToneSynthParams = {
  "synth/type": { type: "list", value: "sine", list: OscTypes } as ParamList<typeof OscTypes>,
  "synth/frequency": { type: "float", value: 440, min: 20, max: 20000, step: 1 },
  "synth/detune": { type: "float", value: 0, min: -1200, max: 1200, step: 1 },
  "synth/duration": { type: "float", value: 1, min: 0.1, max: 10, step: 0.1 },
} satisfies ParamDict;
export type ToneSynthOpts = ParamDictProps<typeof ToneSynthParams>;

export const Tone = (props: Partial<ToneSynthOpts> & SynthVoiceProps) => {
  const o = SynthOpts(ToneSynth, props);
  return (
    <gain gain={props.velocity ?? 1}>
      <osc
        type={o["synth/type"]}
        frequency={o["synth/frequency"]}
        detune={o["synth/detune"]}
        stop={o["synth/duration"]}
        onEnded={props.onEnded}
      />
    </gain>
  );
};

export const ToneSynth: SynthDef<typeof ToneSynthParams> = {
  label: "tone",
  params: ToneSynthParams,
  Component: Tone,
  duration: (o) => o["synth/duration"],
};
//#endregion

//#region multi osc
export const MultiOscSynthParams = {
  "osc/type": { type: "list", value: "sine", list: OscTypes } as ParamList<typeof OscTypes>,
  "osc/frequency": { type: "float", value: 440, min: 20, max: 20000, step: 1 },
  "osc/detune": { type: "float", value: 0, min: -1200, max: 1200, step: 1 },
  "osc/ratios": { type: "string", value: "1 2 3" },
  "osc/duration": { type: "float", value: 1, min: 0.1, max: 10, step: 0.1 },
  "playback/rampUp": { type: "float", value: 0.05, min: 0.01, max: 2, step: 0.01 },
  "playback/rampDown": { type: "float", value: 0.05, min: 0.01, max: 2, step: 0.01 },
} satisfies ParamDict;
export type MultiOscSynthOpts = ParamDictProps<typeof MultiOscSynthParams>;

export const MultiOscTone = (props: Partial<MultiOscSynthOpts> & SynthVoiceProps) => {
  const o = SynthOpts(MultiOscSynth, props);
  const duration = o["osc/duration"];
  const ratios = useMemo(() => {
    const parsed = ParseNumbers(o["osc/ratios"]);
    return parsed.length ? parsed : [1];
  }, [o["osc/ratios"]]);
  return (
    <gain
      gain={FadeEnv(
        0.8 * (props.velocity ?? 1),
        o["playback/rampUp"],
        o["playback/rampDown"],
        duration,
      )}
    >
      <MultiOsc
        type={o["osc/type"]}
        frequency={o["osc/frequency"]}
        detune={o["osc/detune"]}
        ratios={ratios}
        stop={duration}
        onEnded={props.onEnded}
      />
    </gain>
  );
};

export const MultiOscSynth: SynthDef<typeof MultiOscSynthParams> = {
  label: "multi osc",
  params: MultiOscSynthParams,
  Component: MultiOscTone,
  duration: (o) => o["osc/duration"],
};
//#endregion
